import {useBoardLabels} from '@github-ui/board-core'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'

import styles from './BoardSkeletons.module.css'

/** How many placeholder cards to draw when the caller states no page size. */
const FALLBACK_PAGE_SIZE = 3

export type BoardCardSkeletonProps = {
  className?: string
}

/**
 * One placeholder card.
 *
 * Carries a test id because it has no role or name to be found by: the bars are
 * decorative and the card is hidden from assistive technology, so a test that checks a
 * cell drew a page-shaped skeleton has nothing semantic to locate.
 */
export function BoardCardSkeleton({className}: BoardCardSkeletonProps) {
  return (
    <div className={clsx(styles.CardSkeleton, className)} data-testid="board-card-skeleton" aria-hidden>
      <LoadingSkeleton height="sm" width="random" variant="rounded" />
      <LoadingSkeleton height="sm" width="40%" variant="rounded" />
    </div>
  )
}

export type BoardCardSkeletonsProps = {
  /** `pageSize` from the board's data. A page arrives whole, so the skeleton is page-shaped. */
  count?: number
  className?: string
}

/**
 * A page of placeholder cards.
 *
 * The skeleton is page-shaped rather than card-shaped: a page of cards is loaded and
 * returned whole, so the transition is from N placeholders to N cards. A single
 * placeholder would imply cards trickle in one at a time, which is the impression the
 * loading model exists to avoid.
 *
 * The label is a real text node rather than an `aria-label` on the container. The bars
 * are decorative and hidden, so a container named by an attribute would carry a name
 * with no content behind it and be dropped from the reading order entirely.
 */
export function BoardCardSkeletons({count, className}: BoardCardSkeletonsProps) {
  const labels = useBoardLabels()
  const total = count ?? FALLBACK_PAGE_SIZE

  return (
    <div className={clsx(styles.CardSkeletons, className)}>
      <VisuallyHidden>{labels.loadingItems}</VisuallyHidden>
      {Array.from({length: total}, (_, index) => (
        <BoardCardSkeleton key={index} />
      ))}
    </div>
  )
}

export type BoardColumnSkeletonProps = {
  className?: string
}

/** A ghost column, drawn while a page of columns is in flight. */
export function BoardColumnSkeleton({className}: BoardColumnSkeletonProps) {
  const labels = useBoardLabels()

  return (
    <div className={clsx(styles.ColumnSkeleton, className)}>
      <VisuallyHidden>{labels.loadingColumns}</VisuallyHidden>
      <LoadingSkeleton height="md" variant="rounded" aria-hidden />
    </div>
  )
}

export type BoardRowSkeletonProps = {
  className?: string
}

/** A ghost row, drawn while a page of rows is in flight. */
export function BoardRowSkeleton({className}: BoardRowSkeletonProps) {
  const labels = useBoardLabels()

  return (
    <div className={clsx(styles.RowSkeleton, className)}>
      <VisuallyHidden>{labels.loadingRows}</VisuallyHidden>
      <LoadingSkeleton height="md" width="30%" variant="rounded" aria-hidden />
    </div>
  )
}
